<template>
  <div v-if="banners.length" class="banner-carousel" data-cy="banner-carousel">
    <swiper-container
      :slides-per-view="1"
      :speed="500"
      :loop="banners.length > 1"
      :autoplay="{
        delay: 10000,
        disableOnInteraction: false,
      }"
      :pagination="{
        clickable: true,
      }">
      <swiper-slide v-for="banner in banners" :key="banner.id">
        <div class="image" :style="{ backgroundImage: `url(${banner.imageUrl})` }">
          <div class="teaser-container">
            <div class="text" data-cy="teaser-text">
              <div class="title" data-cy="teaser-title">
                {{ banner.title[userLanguage] ?? banner.title[fallbackLocale] }}
              </div>
              <div class="subtitle" data-cy="teaser-subtitle" v-if="banner.subtitle">
                {{ banner.subtitle[userLanguage] ?? banner.subtitle[fallbackLocale] }}
              </div>
              <RouterLink
                v-if="!isExternalLink(getBannerLink(banner))"
                class="see-more-button"
                :to="getBannerLink(banner)">
                <BackstageButton
                  icon-right="chevron-right"
                  data-cy="see-more-button-cnt"
                  data-cy-button="see-more-button">
                  {{ $t('marketing.banner_button') }}
                </BackstageButton>
              </RouterLink>
              <a v-else class="see-more-button" :href="getBannerLink(banner)" target="_blank" rel="noopener noreferrer">
                <BackstageButton
                  icon-right="chevron-right"
                  data-cy="see-more-button-cnt"
                  data-cy-button="see-more-button">
                  {{ $t('marketing.banner_button') }}
                </BackstageButton>
              </a>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper-container>
    <div class="scroll-icon" data-cy="scroll-icon" v-on:click="scrollToId(scrollTargetId)"></div>
  </div>
</template>

<script lang="ts">
import { utilsMixins } from '@/mixins/utils.mixins';
import { BannerSectionModel } from '@/models/banner-section.model';
import { BannerModel } from '@/models/banner.model';
import { BannersService } from '@/services/banners.service';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useLocale } from '../../composables/locale';
import { globalConstants } from '../../constants/global.constants';
import { useUserStore } from '../../stores/user';
import { isDateAfterOrEqual, isDateBeforeOrEqual, today, toPlainDate } from '../../utils/temporalUtils';
import BackstageButton from './BackstageButton.vue';

export default defineComponent({
  name: 'banner-carousel',
  components: {
    BackstageButton,
    RouterLink,
  },
  props: {
    sectionType: {
      required: true,
      type: String,
      default: '',
    },
    scrollTargetId: {
      required: false,
      type: String,
      default: '',
    },
  },
  setup() {
    const { userLanguage, userLocale } = useLocale();
    const { selectedAccountId } = storeToRefs(useUserStore());
    const route = useRoute();

    return {
      userLanguage,
      userLocale,
      selectedAccountId,
      route,
    };
  },
  data() {
    return {
      options: {
        dots: true,
        fade: true,
        autoplay: true,
        autoplaySpeed: 8000,
      },
      banners: new Array<BannerModel>(),
      bannersService: new BannersService(),
      section: new BannerSectionModel(),
      fallbackLocale: globalConstants.fallBackLocale,
    };
  },
  watch: {
    selectedAccountId: {
      immediate: true,
      handler(newVal: string | undefined, oldVal: string | undefined) {
        if (newVal && newVal !== oldVal) {
          this.init();
        }
      },
    },
  },
  methods: {
    ...utilsMixins,
    async init() {
      const result = await this.bannersService.getBannerSections();

      if (result.isValid() && result.model) {
        const section = result.model.find((bannerSection) => bannerSection.type === this.sectionType);

        if (section?.externalId) {
          this.section = section as BannerSectionModel;
          this.fetchBanners();
        }
      }
    },
    async fetchBanners() {
      const validation = await this.bannersService.getBannersByAccountId(
        this.section.externalId,
        this.selectedAccountId,
      );

      if (validation.isValid() && validation.model) {
        this.banners = this.filterActiveBanners(validation.model);

        // ToDo check why route sometimes can be undefined
        this.attemptAutoScroll(this.scrollTargetId, this.route?.query);
      }
    },
    filterActiveBanners(banners: BannerModel[]) {
      const currentDate = today();

      return banners.filter(
        (model) =>
          isDateAfterOrEqual(currentDate, toPlainDate(model.startDate[globalConstants.fallBackLocale])) &&
          (!model.endDate ||
            !model.endDate[globalConstants.fallBackLocale] ||
            isDateBeforeOrEqual(currentDate, toPlainDate(model.endDate[globalConstants.fallBackLocale]))),
      );
    },
    getBannerLink(banner: BannerModel): string {
      return banner.linkUrl?.[this.userLanguage] ?? banner.linkUrl?.[this.fallbackLocale] ?? '';
    },
    isExternalLink(link: string): boolean {
      return /^https?:\/\//i.test(link);
    },
  },
});
</script>
<style scoped lang="scss">
.banner-carousel:deep() {
  max-width: 1920px;
  margin: 0 auto;
  padding: 0px !important;
  --swiper-pagination-bottom: 30px;
  --swiper-pagination-bullet-width: 30px;
  --swiper-pagination-bullet-height: 3px;
  --swiper-pagination-bullet-border-radius: 0;

  swiper-container {
    position: relative;
    display: block;
    box-sizing: border-box;
    user-select: none;
    touch-action: pan-y;
    overflow: hidden;

    &.swiper-slide-active {
      swiper-slide {
        display: block;

        &.swiper-slide-active {
          z-index: 2;
        }
      }
    }

    .bullet-container {
      display: flex;
    }

    .image {
      position: relative;
      width: 100%;
      aspect-ratio: 3 / 1;
      background-repeat: no-repeat;
      background-size: cover;
      background-position-x: right;
      border-radius: 0px 0px 4px 4px;

      .teaser-container {
        max-width: 1440px;

        .text {
          color: var(--secondary-text-color);
          position: absolute;
          left: 28px;
          bottom: 60px;
          max-width: 50%;
          text-align: left;

          .title {
            font-size: 1rem;
          }

          .subtitle {
            font-size: 2.5rem;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;

            @include breakpoint(untilxlarge) {
              font-size: 1.5rem;
            }
          }
        }

        .see-more-button {
          display: block;
          padding-top: 30px;

          @include breakpoint(medium) {
            padding-top: 10px;
          }
        }
      }
    }
  }

  .scroll-icon {
    padding: 20px;
    display: inline-block;
    align-items: center;
    transform: translateY(-55%) rotate(180deg);
    cursor: pointer;
    background-image: url('@/assets/img/arrow-round.svg');
    z-index: 1;
    position: relative;
  }
}
</style>
